/**
 * Temporary page to initialize database roles
 * Access at: /init-roles
 * Delete this file after use
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export default function InitRoles() {
  const { currentUser } = useAuth();
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const initializeRoles = async () => {
    setLoading(true);
    setStatus('Initializing...');

    try {
      const token = await currentUser?.getIdToken();

      const response = await fetch('https://dev.foxie.systems/api/admin/initialize-roles', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setStatus(`✅ Success: ${data.message}`);
      } else {
        setStatus(`❌ Error: ${data.error}`);
      }
    } catch (error: any) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Not Signed In
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Please sign in as an administrator to initialize roles
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Initialize Database Roles
        </h1>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Signed in as:
          </p>
          <p className="font-medium text-gray-900 dark:text-white">
            {currentUser.email}
          </p>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            This will create the default roles:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <li>• <strong>customer</strong>: product:read, order:read</li>
            <li>• <strong>administrator</strong>: product:*, user:*, order:*, analytics:*, settings:*, audit:*</li>
          </ul>
        </div>

        <button
          onClick={initializeRoles}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Initializing...' : 'Initialize Roles'}
        </button>

        {status && (
          <div className={`mt-4 p-4 rounded-lg ${
            status.includes('✅')
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
          }`}>
            <p className="text-sm font-medium">{status}</p>
          </div>
        )}

        <div className="mt-6 text-center">
          <a
            href="/admin"
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            Go to Admin Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
